import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider, AuthContext } from './contexts/AuthContext';

// Public Pages
import LandingPage from './pages/public/LandingPage';
import CourseGroupsDetailsPage from './pages/public/CourseGroupsDetailsPage';
import LoginPage from './pages/public/LoginPage';
import UnderMaintenance from './pages/public/UnderMaintenance';

// Private Pages
import Dashboard from './pages/private/dashboard/Dashboard';
import AdminLayout from './layouts/AdminLayout';

// Branch Management
import BranchList from './pages/private/branches/BranchList';
import BranchCreate from './pages/private/branches/BranchCreate';
import BranchEdit from './pages/private/branches/BranchEdit';

// Course Management
import CourseList from './pages/private/courses/CourseList';
import CourseCreate from './pages/private/courses/CourseCreate';
import CourseEdit from './pages/private/courses/CourseEdit';
import CategoryList from './pages/private/courses/CategoryList';

// Student Management
import StudentList from './pages/private/students/StudentList';
import StudentCreate from './pages/private/students/StudentCreate';
import StudentEdit from './pages/private/students/StudentEdit';
import SubmitStudents from './pages/private/students/SubmitStudents';
import PendingApprovals from './pages/private/students/PendingApprovals';
import RejectedApplications from './pages/private/students/RejectedApplications';
import AlumniList from './pages/private/students/AlumniList';
import StudentPrint from './pages/private/students/StudentPrint';

// Examination Management
import ExaminationList from './pages/private/examinations/ExaminationList';
import ExaminationCreate from './pages/private/examinations/ExaminationCreate';
import ExaminationView from './pages/private/examinations/ExaminationView';
import ExaminationStats from './pages/private/examinations/ExaminationStats';
import QuestionList from './pages/private/examinations/QuestionList';
import StudentAssignment from './pages/private/examinations/StudentAssignment';
import PendingApprovalsExam from './pages/private/examinations/PendingApprovals';
import ApprovedStudents from './pages/private/examinations/ApprovedStudents';
import RejectedStudents from './pages/private/examinations/RejectedStudents';
import ExamResults from './pages/private/examinations/ExamResults';
import CenterwiseStudentList from './pages/private/examinations/CenterwiseStudentList';

// Student Pages
import ExamDashboard from './pages/private/student/ExamDashboard';
import TakeExam from './pages/private/student/TakeExam';
import MyResults from './pages/private/student/MyResults';

// Settings
import Settings from './pages/private/settings/Settings';
import EmailReset from './pages/private/settings/EmailReset';

// Franchise
import FranchiseEnquiries from './pages/private/franchise/FranchiseEnquiries';

// Consultation
import ConsultationRequests from './pages/private/consultation/ConsultationRequests';

// Payments
import PaymentList from './pages/private/payments/PaymentList';

// Batches
import BatchList from './pages/private/batches/BatchList';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = React.useContext(AuthContext);

  if (loading) {
    return <div>Loading...</div>;
  }

  return isAuthenticated ? children : <Navigate to="/login" />;
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Router
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/course-groups" element={<CourseGroupsDetailsPage />} />
            {/* <Route path="/" element={<UnderMaintenance />} /> */}
            <Route path="/login" element={<LoginPage />} />

            {/* Protected Routes */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Dashboard />} />
              <Route path="dashboard" element={<Dashboard />} />

              {/* Branches */}
              <Route path="branches" element={<BranchList />} />
              <Route path="branches/create" element={<BranchCreate />} />
              <Route path="branches/edit/:id" element={<BranchEdit />} />

              {/* Courses */}
              <Route path="courses" element={<CourseList />} />
              <Route path="courses/create" element={<CourseCreate />} />
              <Route path="courses/edit/:id" element={<CourseEdit />} />
              <Route path="courses/categories" element={<CategoryList />} />

              {/* Students */}
              <Route path="students" element={<StudentList />} />
              <Route path="students/create" element={<StudentCreate />} />
              <Route path="students/edit/:id" element={<StudentEdit />} />
              <Route path="students/submit" element={<SubmitStudents />} />
              <Route path="students/pending" element={<PendingApprovals />} />
              <Route path="students/rejected" element={<RejectedApplications />} />
              <Route path="students/alumni" element={<AlumniList />} />
              <Route path="students/print" element={<StudentPrint />} />

              {/* Batches */}
              <Route path="batches" element={<BatchList />} />

              {/* Email Reset */}
              <Route path="email-reset" element={<EmailReset />} />

              {/* Franchise */}
              <Route path="franchise-enquiries" element={<FranchiseEnquiries />} />

              {/* Consultation */}
              <Route path="consultation-requests" element={<ConsultationRequests />} />

              {/* Payments */}
              <Route path="payments" element={<PaymentList />} />

              {/* Examinations */}
              <Route path="examinations" element={<ExaminationList />} />
              <Route path="examinations/create" element={<ExaminationCreate />} />
              <Route path="examinations/edit/:id" element={<ExaminationCreate />} />
              <Route path="examinations/:id/stats" element={<ExaminationStats />} />
              <Route path="examinations/:id" element={<ExaminationView />} />
              <Route path="examinations/questions" element={<QuestionList />} />
              <Route path="examinations/assign-students" element={<StudentAssignment />} />
              <Route path="examinations/pending-approvals" element={<PendingApprovalsExam />} />
              <Route path="examinations/approved-students" element={<ApprovedStudents />} />
              <Route path="examinations/rejected-students" element={<RejectedStudents />} />
              <Route path="examinations/results" element={<ExamResults />} />
              <Route path="examinations/centerwise-print" element={<CenterwiseStudentList />} />

              {/* Settings */}
              <Route path="settings" element={<Settings />} />
            </Route>

            {/* Student Routes */}
            <Route
              path="/student"
              element={
                <ProtectedRoute>
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/student/dashboard" replace />} />
              <Route path="dashboard" element={<ExamDashboard />} />
              <Route path="exam/:examinationId" element={<TakeExam />} />
              <Route path="results" element={<MyResults />} />
            </Route>
          </Routes>
        </Router>
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
